import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'app/AppStore';
import { getTechnicalCharacteristicsLabel } from 'components/StockInfo/catalogStockUtils';
import { getLastVehicleDetail } from 'domains/catalog/Catalog.store';
import { RENATIVE_LABEL, RENATIVE_TECHNICAL_CHARACTERISTICS_CODE } from 'domains/catalog/Catalog.types';
import { DHReferenceLocal, getReference3dModel, sendGetReference3dModelRequestSaga } from 'domains/references';
import { FOUND, getSearchData, isLoading } from 'utils';
import { ProductGalleryAsset, ProductGalleryState } from './ProductGallery.types';

export const useProductGallery = (refLocal: DHReferenceLocal): ProductGalleryState => {
  const dispatch = useDispatch();
  const referenceNumber = refLocal.referenceNumber ?? '';
  const vehicleBrand = useSelector(getLastVehicleDetail)?.vehicleBrand ?? 'OTHER';
  const model3d = useSelector((state: RootState) => getReference3dModel(state, referenceNumber));
  const jsonUrl = getSearchData(model3d);
  const [json, setJson] = useState<string | undefined>(undefined);
  const [loading3d, setLoading3d] = useState(false);

  useEffect(() => {
    dispatch(sendGetReference3dModelRequestSaga({ reference: referenceNumber }));
  }, [dispatch, referenceNumber]);

  const imageAssets = useMemo<ProductGalleryAsset[]>(
    () =>
      (refLocal.imageKeys ?? [])
        .filter(Boolean)
        .map((imageKey, index) => ({ id: `image-${index}-${imageKey}`, type: 'image', imageKey })),
    [refLocal.imageKeys],
  );

  const has3dAsset = !model3d || isLoading(model3d) || model3d.searchStatus === FOUND;
  const assets = useMemo<ProductGalleryAsset[]>(
    () => (has3dAsset ? [...imageAssets, { id: 'product-3d', type: '3d' }] : imageAssets),
    [has3dAsset, imageAssets],
  );

  const load3dModel = () => {
    if (!jsonUrl || json || loading3d) {
      return;
    }
    setLoading3d(true);
    fetch(jsonUrl)
      .then((response) => response.text())
      .then((response) => {
        setJson(response);
        setLoading3d(false);
      })
      .catch(() => {
        setLoading3d(false);
      });
  };

  return {
    assets,
    catalogSource: refLocal.source === 'dh' ? 'DATAHUB' : 'IAM',
    familyCode: refLocal.familyCode,
    hasOnly3dAsset: assets.length === 1 && assets[0]?.type === '3d',
    json,
    loading3d,
    model3d,
    referenceBrand: refLocal.brand,
    referenceNumber,
    renative: getTechnicalCharacteristicsLabel(refLocal, RENATIVE_TECHNICAL_CHARACTERISTICS_CODE) === RENATIVE_LABEL,
    tireBrandId: refLocal.tireDetails?.brandId,
    vehicleBrand,
    load3dModel,
  };
};
